<template>
  <div class="login-container">
    <div class="login-orb login-orb-one"></div>
    <div class="login-orb login-orb-two"></div>
    <div class="login-orb login-orb-three"></div>

    <section class="login-hero" aria-label="Product overview">
      <img class="brand-mark" :src="brandLogo" alt="BeyondWebsite.ai" />
      <p class="eyebrow">Early access</p>
      <h1>Build websites that rank, convert, and stay current.</h1>
      <p class="hero-copy">
        SEO, AEO, and GEO built in from day one — not bolted on as an afterthought. Ship faster, get found sooner, and keep your site sharp without the grind.
      </p>

      <div class="hero-metrics">
        <div>
          <span>Discovery</span>
          <strong>SEO-ready</strong>
        </div>
        <div>
          <span>AI answers</span>
          <strong>AEO-aware</strong>
        </div>
        <div>
          <span>Generative search</span>
          <strong>GEO-minded</strong>
        </div>
      </div>

      <div class="hero-panel">
        <div class="hero-panel-top">
          <span class="status-dot"></span>
          <span>Workspace activity</span>
        </div>
        <div class="activity-row">
          <span>Generate site shell</span>
          <strong>Complete</strong>
        </div>
        <div class="activity-row">
          <span>Apply design system</span>
          <strong>Running</strong>
        </div>
        <div class="activity-row muted">
          <span>Publish preview</span>
          <strong>Queued</strong>
        </div>
      </div>
    </section>

    <div class="login-card">

      <!-- Success state: registration submitted -->
      <div v-if="verificationPending" class="verification-pending">
        <div class="verification-icon">🎉</div>
        <h3>You're on the list!</h3>
        <p>We received your request for <strong>{{ pendingOrg }}</strong>. Our team will review it and email <strong>{{ pendingEmail }}</strong> with access credentials — usually within 1–2 business days.</p>
        <p class="verification-sub">Questions? Reply to the confirmation email we just sent you.</p>
      </div>

      <!-- Normal card: header + tabs + forms -->
      <template v-else>
        <div class="header">
          <p class="eyebrow">{{ activeTab === 'login' ? 'Secure access' : 'Early access' }}</p>
          <h2>{{ activeTab === 'login' ? 'Welcome back' : 'Request access' }}</h2>
          <p>{{ activeTab === 'login' ? 'Sign in to continue building.' : 'Tell us about your team and we\'ll get you set up.' }}</p>
        </div>

        <!-- Tab Navigation -->
        <div class="tab-nav">
          <button
            :class="{ active: activeTab === 'login' }"
            @click="activeTab = 'login'"
          >
            Sign in
          </button>
          <button
            :class="{ active: activeTab === 'register' }"
            @click="activeTab = 'register'"
          >
            Sign up
          </button>
        </div>

        <!-- Login Form -->
        <div v-if="activeTab === 'login'" class="form-container">

          <!-- Forgot password flow -->
          <div v-if="forgotMode">
            <div v-if="forgotSuccess" class="forgot-success">
              <div style="font-size:32px;margin-bottom:8px">📬</div>
              <p>If that email is registered, a reset link is on its way. Check your inbox.</p>
              <button class="forgot-back-btn" @click="forgotMode=false;forgotSuccess=false">← Back to sign in</button>
            </div>
            <form v-else @submit.prevent="handleForgotPassword" class="auth-form">
              <div class="form-group">
                <label for="forgot-email">Your email address</label>
                <input
                  id="forgot-email"
                  v-model="forgotEmail"
                  type="email"
                  required
                  :disabled="loading"
                  placeholder="you@company.com"
                  autofocus
                />
              </div>
              <div v-if="error" class="error-message">{{ error }}</div>
              <button type="submit" :disabled="loading" class="submit-btn">
                {{ loading ? 'Sending…' : 'Send reset link' }}
              </button>
              <button type="button" class="forgot-back-btn" @click="forgotMode=false;error=''">← Back to sign in</button>
            </form>
          </div>

          <form v-else @submit.prevent="handleLogin" class="auth-form">
            <div class="form-group">
              <label for="login-email">Email</label>
              <input
                id="login-email"
                v-model="loginForm.email"
                type="email"
                required
                :disabled="loading"
                placeholder="you@company.com"
              />
            </div>

            <div class="form-group">
              <div style="display:flex;justify-content:space-between;align-items:center">
                <label for="login-password">Password</label>
                <button type="button" class="forgot-link" @click="forgotMode=true;forgotEmail=loginForm.email;error=''">Forgot password?</button>
              </div>
              <input
                id="login-password"
                v-model="loginForm.password"
                type="password"
                required
                :disabled="loading"
                placeholder="Your password"
              />
            </div>

            <button type="submit" :disabled="loading" class="submit-btn">
              {{ loading ? 'Signing in…' : 'Sign in' }}
            </button>
          </form>
        </div>

        <!-- Register Form -->
        <div v-if="activeTab === 'register'" class="form-container">
          <form @submit.prevent="handleRegister" class="auth-form">
            <div class="form-group">
              <label for="org-name">Organization</label>
              <input
                id="org-name"
                v-model="registerForm.organizationName"
                type="text"
                required
                :disabled="loading"
                placeholder="Your company or project name"
              />
            </div>

            <div class="form-group">
              <label for="reg-name">Your name</label>
              <input
                id="reg-name"
                v-model="registerForm.adminName"
                type="text"
                required
                :disabled="loading"
                placeholder="Full name"
              />
            </div>

            <div class="form-group">
              <label for="reg-email">Work email</label>
              <input
                id="reg-email"
                v-model="registerForm.adminEmail"
                type="email"
                required
                :disabled="loading"
                placeholder="you@company.com"
              />
            </div>

            <button type="submit" :disabled="loading" class="submit-btn">
              {{ loading ? 'Submitting…' : 'Request access' }}
            </button>
          </form>
        </div>

        <!-- Error Messages -->
        <div v-if="error" class="error-message">
          {{ error }}
          <div v-if="loginVerificationRequired" class="pending-review-note">
            Your account is pending manual review. We'll email you when it's ready.
          </div>
        </div>
      </template>

      <p class="powered-by">Powered by <a href="https://feature1.ai" target="_blank" rel="noopener noreferrer">feature1.ai</a></p>
    </div>
  </div>
</template>

<script>
import { ref, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useSitesStore } from '../stores/sites'
import api from '../utils/api'
import brandLogo from '../assets/bw-logo-wordmark.svg'

export default {
  name: 'Login',
  setup() {
    const router = useRouter()
    const sitesStore = useSitesStore()

    const activeTab = ref('login')
    const loading = ref(false)
    const error = ref('')
    const verificationPending = ref(false)
    const pendingEmail = ref('')
    const pendingOrg = ref('')
    const loginVerificationRequired = ref(false)
    const forgotMode = ref(false)
    const forgotEmail = ref('')
    const forgotSuccess = ref(false)

    const loginForm = reactive({
      email: '',
      password: ''
    })

    const registerForm = reactive({
      organizationName: '',
      adminName: '',
      adminEmail: '',
    })

    watch(activeTab, () => {
      error.value = ''
      loginVerificationRequired.value = false
      forgotMode.value = false
      forgotSuccess.value = false
    })

    const handleForgotPassword = async () => {
      error.value = ''
      loading.value = true
      try {
        await api.post('/auth/forgot-password', { email: forgotEmail.value })
        forgotSuccess.value = true
      } catch {
        forgotSuccess.value = true // always show success to avoid email enumeration
      } finally {
        loading.value = false
      }
    }

    const handleLogin = async () => {
      error.value = ''
      loginVerificationRequired.value = false
      loading.value = true

      try {
        const response = await api.post('/auth/login', loginForm)

        if (response.data.success) {
          localStorage.setItem('bwai_token', response.data.token)
          localStorage.setItem('bwai_user', JSON.stringify(response.data.user))
          localStorage.setItem('bwai_tenant', JSON.stringify(response.data.tenant))
          api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`
          sitesStore.setUser(response.data.user, response.data.tenant)
          router.push('/sites')
        } else {
          error.value = response.data.error || 'Login failed'
        }
      } catch (err) {
        const data = err.response?.data
        if (data?.emailVerificationRequired) {
          pendingEmail.value = data.email || loginForm.email
          loginVerificationRequired.value = true
          error.value = 'Your account is pending review.'
        } else {
          error.value = data?.error || 'Login failed. Please try again.'
        }
      } finally {
        loading.value = false
      }
    }

    const handleRegister = async () => {
      error.value = ''
      loading.value = true

      try {
        const response = await api.post('/auth/register', registerForm)

        if (response.data.success) {
          pendingEmail.value = registerForm.adminEmail
          pendingOrg.value = registerForm.organizationName
          verificationPending.value = true
        } else {
          error.value = response.data.error || 'Registration failed'
        }
      } catch (err) {
        error.value = err.response?.data?.error || 'Registration failed. Please try again.'
      } finally {
        loading.value = false
      }
    }

    return {
      activeTab,
      brandLogo,
      loading,
      error,
      verificationPending,
      pendingEmail,
      pendingOrg,
      loginVerificationRequired,
      forgotMode, forgotEmail, forgotSuccess,
      loginForm,
      registerForm,
      handleLogin,
      handleRegister,
      handleForgotPassword,
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 480px);
  align-items: center;
  gap: clamp(2rem, 6vw, 6rem);
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at 18% 18%, rgba(124,109,250,0.24), transparent 30%),
    radial-gradient(circle at 82% 72%, rgba(52,211,153,0.14), transparent 34%),
    linear-gradient(135deg, #0f0f18 0%, #141420 48%, #090911 100%);
  padding: clamp(1.25rem, 4vw, 4rem);
  color: var(--text-primary);
}

.login-container::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px);
  background-size: 72px 72px;
  mask-image: radial-gradient(circle at center, black 0%, transparent 72%);
}

.login-orb {
  position: absolute;
  border-radius: 999px;
  filter: blur(60px);
  pointer-events: none;
  will-change: transform;
}

.login-orb-one {
  width: 560px;
  height: 560px;
  top: -160px;
  right: 0%;
  background: radial-gradient(circle, rgba(124,109,250,.55) 0%, rgba(124,109,250,.18) 45%, transparent 70%);
  animation: login-drift-1 18s ease-in-out infinite;
}

.login-orb-two {
  width: 480px;
  height: 480px;
  bottom: -120px;
  left: -60px;
  filter: blur(72px);
  background: radial-gradient(circle, rgba(52,211,153,.45) 0%, rgba(52,211,153,.12) 45%, transparent 70%);
  animation: login-drift-2 22s ease-in-out infinite;
}

.login-orb-three {
  width: 520px;
  height: 520px;
  /* centre of orb sits under the bottom-left corner of the card column */
  bottom: -80px;
  right: calc(clamp(360px, 40vw, 480px) + clamp(1.25rem, 4vw, 4rem) - 260px);
  filter: blur(76px);
  background: radial-gradient(circle, rgba(236,72,153,.38) 0%, rgba(236,72,153,.10) 45%, transparent 70%);
  animation: login-drift-3 28s ease-in-out infinite;
}

@keyframes login-drift-1 {
  0%   { transform: translate(0,      0)      scale(1);    opacity: .90; }
  25%  { transform: translate(-90px,  75px)   scale(1.14); opacity: 1;   }
  55%  { transform: translate( 70px, -65px)   scale(0.88); opacity: .78; }
  80%  { transform: translate(-50px,  95px)   scale(1.10); opacity: .95; }
  100% { transform: translate(0,      0)      scale(1);    opacity: .90; }
}

@keyframes login-drift-2 {
  0%   { transform: translate(0,      0)      scale(1);    opacity: .80; }
  30%  { transform: translate( 85px, -75px)   scale(1.16); opacity: 1;   }
  60%  { transform: translate(-65px,  55px)   scale(0.86); opacity: .72; }
  85%  { transform: translate( 50px, -90px)   scale(1.12); opacity: .88; }
  100% { transform: translate(0,      0)      scale(1);    opacity: .80; }
}

@keyframes login-drift-3 {
  0%   { transform: translate(0,      0)      scale(1);    opacity: .70; }
  35%  { transform: translate( 75px,  65px)   scale(1.20); opacity: .90; }
  65%  { transform: translate(-70px, -60px)   scale(0.82); opacity: .60; }
  100% { transform: translate(0,      0)      scale(1);    opacity: .70; }
}

.login-hero,
.login-card {
  position: relative;
  z-index: 1;
}

.login-hero {
  max-width: 720px;
}

.brand-mark {
  width: min(340px, 78vw);
  height: auto;
  border-radius: 0;
  display: block;
  object-fit: contain;
  margin-bottom: 1.5rem;
}

.eyebrow {
  margin: 0 0 .75rem;
  color: var(--accent);
  font-size: .72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .16em;
}

.login-hero h1 {
  margin: 0;
  max-width: 680px;
  color: #fff;
  font-size: clamp(2.75rem, 6vw, 5.6rem);
  line-height: .92;
  letter-spacing: -.07em;
  font-weight: 850;
}

.hero-copy {
  max-width: 560px;
  margin: 1.5rem 0 0;
  color: var(--text-secondary);
  font-size: clamp(1rem, 1.4vw, 1.18rem);
  line-height: 1.75;
}

.hero-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: .75rem;
  margin-top: 2rem;
  max-width: 620px;
}

.hero-metrics div,
.hero-panel {
  background: var(--glass-bg);
  border: var(--glass-border);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  box-shadow: var(--elev-sm);
}

.hero-metrics div {
  border-radius: 16px;
  padding: .9rem 1rem;
}

.hero-metrics span {
  display: block;
  color: var(--text-muted);
  font-size: .72rem;
  margin-bottom: .28rem;
}

.hero-metrics strong {
  display: block;
  color: var(--text-primary);
  font-size: .94rem;
}

.hero-panel {
  max-width: 520px;
  margin-top: 1rem;
  border-radius: 22px;
  padding: 1rem;
}

.hero-panel-top,
.activity-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.hero-panel-top {
  justify-content: flex-start;
  color: var(--text-muted);
  font-size: .78rem;
  padding-bottom: .85rem;
  border-bottom: var(--glass-border);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: var(--success);
  box-shadow: 0 0 14px rgba(52,211,153,.7);
}

.activity-row {
  padding: .85rem .25rem 0;
  color: var(--text-secondary);
  font-size: .86rem;
}

.activity-row strong {
  color: var(--text-primary);
  font-size: .78rem;
}

.activity-row.muted {
  opacity: .58;
}

.login-card {
  background: rgba(22,22,38,.78);
  border: var(--glass-border-hi);
  border-radius: 28px;
  box-shadow: var(--elev-lg), 0 0 0 1px rgba(124,109,250,.08);
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
  padding: clamp(1.5rem, 3vw, 2.5rem);
  width: 100%;
  overflow: hidden;
}

.login-card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(135deg, rgba(255,255,255,.12), transparent 34%);
  opacity: .8;
}

.header {
  position: relative;
  margin-bottom: 1.5rem;
}

.header h2 {
  color: var(--text-primary);
  margin: 0 0 .5rem;
  font-size: 2rem;
  line-height: 1.05;
  letter-spacing: -.04em;
  font-weight: 780;
}

.header p {
  color: var(--text-muted);
  margin: 0;
  font-size: .94rem;
}

.tab-nav {
  display: flex;
  gap: .35rem;
  margin-bottom: 1.5rem;
  padding: .35rem;
  border-radius: 999px;
  background: var(--glass-bg);
  border: var(--glass-border);
}

.tab-nav button {
  flex: 1;
  padding: .72rem 1rem;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--text-muted);
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.tab-nav button.active {
  color: #fff;
  background: var(--glass-bg-active);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.08), 0 8px 22px rgba(0,0,0,.22);
}

.tab-nav button:hover {
  color: var(--text-primary);
}

.form-container {
  margin-bottom: 1rem;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 700;
  color: var(--text-secondary);
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: .08em;
}

.form-group input {
  padding: .86rem 1rem;
  border: var(--glass-border);
  border-radius: 14px;
  background: rgba(255,255,255,.07);
  color: var(--text-primary);
  font-size: .95rem;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
}

.form-group input::placeholder {
  color: var(--text-muted);
}

.form-group input:focus {
  outline: none;
  border: var(--glass-border-hi);
  background: rgba(255,255,255,.1);
  box-shadow: 0 0 0 4px rgba(124,109,250,.14);
}

.form-group input:disabled {
  opacity: .62;
  cursor: not-allowed;
}

.submit-btn {
  padding: .9rem 1.5rem;
  background: linear-gradient(135deg, var(--accent), #34d399);
  color: white;
  border: none;
  border-radius: 999px;
  font-size: .96rem;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 16px 34px rgba(124,109,250,.25);
  font-family: inherit;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 14px 26px rgba(79, 70, 229, 0.25);
}

.submit-btn:disabled {
  opacity: .55;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.error-message {
  background: rgba(248,113,113,.12);
  color: var(--danger);
  border: 1px solid rgba(248,113,113,.28);
  padding: .85rem 1rem;
  border-radius: 12px;
  margin-top: 1rem;
  font-size: 0.875rem;
}

.pending-review-note {
  margin-top: 8px;
  font-size: 12px;
  color: #a89cff;
  font-style: italic;
}

.verification-pending {
  position: relative;
  padding: 2rem 1.5rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.verification-icon { font-size: 40px; }
.verification-pending h3 { margin: 0; font-size: 1.4rem; font-weight: 780; color: var(--text-primary); letter-spacing: -.03em; }
.verification-pending p  { margin: 0; font-size: 14px; color: var(--text-secondary); line-height: 1.6; max-width: 320px; }
.verification-sub { font-size: 13px !important; color: var(--text-muted) !important; }

.forgot-link {
  background: none;
  border: none;
  color: var(--accent);
  font-size: .78rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  opacity: .85;
  transition: opacity .15s;
}
.forgot-link:hover { opacity: 1; text-decoration: underline; }

.forgot-back-btn {
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: .88rem;
  cursor: pointer;
  padding: .5rem 0 0;
  font-family: inherit;
  display: block;
  text-align: center;
  width: 100%;
  transition: color .15s;
}
.forgot-back-btn:hover { color: var(--text-primary); }

.forgot-success {
  text-align: center;
  padding: 1.5rem 0;
  color: var(--text-secondary);
  font-size: .94rem;
  line-height: 1.65;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.powered-by {
  margin: 1.5rem 0 0;
  text-align: center;
  font-size: .8rem;
  font-weight: 700;
  color: var(--text-secondary);
  letter-spacing: .02em;
}
.powered-by a {
  color: var(--accent);
  text-decoration: none;
}
.powered-by a:hover {
  text-decoration: underline;
}

@media (max-width: 980px) {
  .login-container {
    grid-template-columns: 1fr;
  }

  .login-hero {
    max-width: 680px;
  }

  .hero-panel {
    display: none;
  }
}

@media (max-width: 640px) {
  .login-container {
    padding: 1rem;
  }

  .login-hero h1 {
    font-size: clamp(2.35rem, 14vw, 3.6rem);
  }

  .hero-metrics {
    grid-template-columns: 1fr;
  }

  .login-card {
    border-radius: 22px;
  }
}
</style>
